import { get, map, upperCase, filter, sortBy } from "lodash-es";
import {
    UPI_PACKAGE_NAME_MAPPER,
    MF_SIP_FLOWS,
    MF_ONE_TIME_FLOWS,
    PAYMENT_PRODUCT_NAMES,
    PAYMENT_FLOWS
} from '../constants/payment';
import commonBaseService from './commonBaseService';
import {PAYMENT_NEFT as STRINGS} from '../strings/payment';

const PRODUCT_TYPES = {
	EQUITY: 'equity',
	MF: 'mf',
};	

const SUPPORTED_PAYMENT_OPTIONS = {
	NEFT: 'neft',
	UPI: 'upi',
	NETBANKING: 'netbanking',
};	

export const PAYMENT_TYPE = {
	UPI: 'upi',
	NETBANKING: 'netbanking',
	VPA: 'vpa',
};

const getUpiAppNameFromUpiPackage = (packageName) => {
    const appName = get(UPI_PACKAGE_NAME_MAPPER, `${packageName}`, '');
    return appName;
}

const validateVpaId = (vpaId) => {
   var regex = new RegExp(/^[a-zA-Z0-9._-]{2,}@[a-zA-Z]{2,}$/)
   return regex.test(vpaId);
}

const calculatePaymentPendingExpiryTime = (seconds) => {
	const expiryMinutes = seconds > 0 ? parseInt(seconds / 60, 10) : 0;
	const expirySeconds = seconds > 0 ? seconds % 60 : 0;
	return { expiryMinutes, expirySeconds };
};

// calulates prefilled value for the equity funds
const calculatePrefilledAddFundAmout = (amount = '') => {
    if(commonBaseService.isEmptyOrUndefined(amount)) {
        return ''
    } else if (commonBaseService.isValidNumber(amount)) {
       const  prefilledValue = Math.ceil(amount / 10) * 10;
       return prefilledValue;
    }
}

const formateNeftData = (neftDetails = {}) => {
    return STRINGS.BANK_NEFT_DETAILS(neftDetails);
}

const isEquityProduct = (productName = '') =>
	productName === PRODUCT_TYPES.EQUITY;
const isMfProduct = (productName = '') =>
	productName === PRODUCT_TYPES.MF;

const formatEquityBankLists = ({ bankDetails = [], payment_options = [] }) => {
	let bankLists = [];
	bankLists = map(bankDetails, (el) => {
		const {bank_code = '', ifsc_code = '', bank_acc_no = '', image = '', bank_name = '' } = el;
		const details = {
			bank_code: bank_code,
			bank_name: bank_name,
			image: image,
			bank_acc_no: bank_acc_no,
			ifsc_code: ifsc_code,
			is_primary_bank: true, // default true for equity
			upi_enabled: payment_options.includes(SUPPORTED_PAYMENT_OPTIONS.UPI),
			net_banking_enabled: payment_options.includes(
				SUPPORTED_PAYMENT_OPTIONS.NETBANKING,
			),
			// bank_id: null, //  for equity bank id is not required
		};
		return details;
	});
	return bankLists;
};

const formatSupportedPaymentOptions = ({
	payment_options = [],
	allow_neft = false,
	upi_enabled = false,
	show_netbanking = false,
	productName = '',
}) => {
	const paymentOptions = {};
	if (isEquityProduct(productName)) {
		paymentOptions.neft = payment_options.includes(SUPPORTED_PAYMENT_OPTIONS.NEFT);
		paymentOptions.upi = payment_options.includes(SUPPORTED_PAYMENT_OPTIONS.UPI);
		paymentOptions.net_banking = payment_options.includes(
			SUPPORTED_PAYMENT_OPTIONS.NETBANKING,
		);
	} else if (isMfProduct(productName)) {
		paymentOptions.neft = allow_neft;
		paymentOptions.upi = upi_enabled;
		paymentOptions.net_banking = show_netbanking;
	}

	return paymentOptions;
};

const formatEquityPaymentResponse = ({ data = {}, productName = '', paymentFlow = '' }) => {
	const {
		payment_options = [],
		payment_start_url = '',
		bank_details = [],
		vpa_ids = '',
		amount = '',
	} = data;

	const formattedData = {};

	formattedData['amount'] = amount;
	formattedData['feature_name'] = productName;
	formattedData['paymentFlow'] = paymentFlow;
	formattedData['vpaIds'] = vpa_ids;

	formattedData['supported_payment_options'] = formatSupportedPaymentOptions({
		payment_options,
		productName,
	});

	formattedData['payment_url'] = payment_start_url;

	formattedData['bank_list'] = formatEquityBankLists({
		bankDetails: bank_details,
		payment_options,
	});

	return formattedData;
};

const formatBankAccountNumber = (accountNumber = '') => {
	if (commonBaseService.isEmptyOrUndefined(accountNumber)) {
		return '';
	}
	const accountlastDigits = accountNumber?.slice(
		accountNumber.length - 4,
		accountNumber.length,
	);
	return `XXXX${accountlastDigits}`;
};

const formatMfBankLists = ({ bankDetails = [] }) => {
	let bankLists = [];

	bankLists = map(bankDetails, (el) => {
		const details = {
			bank_code: get(el, 'bank_code', ''),
			bank_name: get(el, 'bank_name', ''),
			image: get(el, 'image', ''),
			bank_acc_no: formatBankAccountNumber(get(el, 'account_number', '')),
			is_primary_bank: get(el, 'is_primary_bank', false),
			upi_enabled: get(el, 'upi_supported', false),
			net_banking_enabled: get(el, 'razorpay_supported', false),
			bank_id: get(el, 'bank_id', ''),
			bank_status: get(el, 'bank_status', ''),
		};
		return details;
	});

	bankLists = filter(bankLists, (el) => el.bank_status === 'verified');
	bankLists = sortBy(bankLists, ({bank_name}) => bank_name);
	return bankLists;
};

const formatMfPaymentResponse = ({ data = {}, productName = '' }) => {
	const {
		allow_neft = false,
		upi_enabled = false,
		show_netbanking = false,
		s2s_razorpay_url = '',
		banks = [],
	} = data;

	const formattedData = {};

	formattedData.amount = data.amount;

	formattedData['supported_payment_options'] = formatSupportedPaymentOptions({
		allow_neft,
		upi_enabled,
		show_netbanking,
		productName,
	});

	formattedData.payment_url = s2s_razorpay_url;

	formattedData.bank_list = formatMfBankLists({
		bankDetails: banks,
	});

	return formattedData;
};

const formateMfOrderCreationResponse = ({back_url = '', callback_url = '', redirect_url = ''}) => ({
	cancel_api_url: back_url,
	polling_url: callback_url,
	redirect_url,
})

const createAddFundsPaymentLandingPayLoad = ({ amount, oc_token, ucc }) => {
	return {
		sessionData: oc_token,
		amount: amount,
		group_id: 'HO',
		ProductName: 'TRADING',
		product_code: '100',
		ucc: upperCase(ucc).replace(' ', ''),
	};
};

const createValidateVpaParams = (vpa_id, save_vpa) => {
	return {
		vpa_id,
		save_vpa,
	};
};
 
const createInitiatePaymentParams = ({ name, type, packageName, redirectUrl }) => {
	if (type === PAYMENT_TYPE.VPA) {
		return {
			vpa_id: name,
			payment_method: PAYMENT_TYPE.UPI,
		};
	} else if (type === PAYMENT_TYPE.UPI) {
		return {
			payment_method: PAYMENT_TYPE.UPI,
			intent: true,
			upi_package: packageName,
		};
	} else if (type === PAYMENT_TYPE.NETBANKING) {
		return {
			payment_method: PAYMENT_TYPE.NETBANKING,
			app_redirect_url: redirectUrl,
		};
	}
};
 
const createPaymentModeSelectionPaylod = ({ name, packageName, type }) => {
	if (type === PAYMENT_TYPE.VPA) {
		return {
			vpa_id: name,
			payment_type: PAYMENT_TYPE.VPA,
		};
	} else if (type === PAYMENT_TYPE.UPI) {
		return {
			payment_type: PAYMENT_TYPE.UPI,
		};
	} else if (type === PAYMENT_TYPE.NETBANKING) {
		return {
			payment_type: PAYMENT_TYPE.NETBANKING,
		};
	}
};

const createInitiateMfPaymentParams = ({name, type, packageName, bankId, redirectUrl }) => {
	if (type === PAYMENT_TYPE.VPA) {
		return {
			vpa: name,
			payment_method: upperCase(PAYMENT_TYPE.UPI),
			is_intent: false,
			bank_id: bankId,
		};
	} else if (type === PAYMENT_TYPE.UPI) {
		return {
			payment_method:  upperCase(PAYMENT_TYPE.UPI),
			is_intent: true,
			upi_package: packageName,
			bank_id: bankId,
		};
	} else if (type === PAYMENT_TYPE.NETBANKING) {
		return {
			payment_method:  upperCase(PAYMENT_TYPE.NETBANKING),
			bank_id: bankId,
			is_intent: false,
			plutus_redirect_url: redirectUrl,
		};
	}
}

const isMfSipFlow = (paymentFlow) => {
	return MF_SIP_FLOWS.includes(paymentFlow);
}

const isMfOnetimeFlow = (paymentFlow) => {
	return MF_ONE_TIME_FLOWS.includes(paymentFlow);
}

const getPrimaryBankDetails = (bankList = []) => {
	const primaryBankDetails = filter(bankList, (item) => get(item, 'is_primary_bank', false))[0];
    return primaryBankDetails
}

const getPaymentInvestType = (paymentFlow, paymentProduct) => {
	switch(paymentProduct) {
		case PAYMENT_PRODUCT_NAMES.EQUITY:
			return PAYMENT_FLOWS.ADD_FUNDS;
		case PAYMENT_PRODUCT_NAMES.MF:
			return getMfInvestmentType(paymentFlow);
		default:
			console.log('unexpected paymentProduct in getPaymentInvestType');		
	}
}

const getMfInvestmentType = (paymentFlow) => {
	if(isMfSipFlow(paymentFlow)) {
       return PAYMENT_FLOWS.SIP
	} else if (isMfOnetimeFlow(paymentFlow)) {
		return PAYMENT_FLOWS.ONETIME
	}
}

const getPaymentCtaText = (paymentProduct, amount) => {
	switch(paymentProduct) {
		case PAYMENT_PRODUCT_NAMES.MF:
            return `Pay ${amount}`;
		case PAYMENT_PRODUCT_NAMES.EQUITY:
		   	 return `Add ${amount}`;
		default:
			return '';		
	}
}

const paymentService = {
    getUpiAppNameFromUpiPackage,
    validateVpaId,
    calculatePaymentPendingExpiryTime,
    calculatePrefilledAddFundAmout,
    formateNeftData,
    formatEquityPaymentResponse,
	formatMfPaymentResponse,
	createAddFundsPaymentLandingPayLoad,
	createValidateVpaParams,
	createInitiatePaymentParams,
	createPaymentModeSelectionPaylod,
	formateMfOrderCreationResponse,
	createInitiateMfPaymentParams,
	getPrimaryBankDetails,
	isMfOnetimeFlow,
	isMfSipFlow,
	getPaymentInvestType,
	getPaymentCtaText,
}

export default paymentService;